import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "@/lib/useReveal";
import { Check, Zap, ArrowRight } from "lucide-react";

/**
 * Landing-page pricing — 2026 simplified single-plan model.
 * One paid plan (Starter), price shown in INR or USD via a toggle.
 * Mirrors backend/plans_catalog.py (₹1,999 GST-incl / $19.99, 15 scans, 15 kw).
 */

const STARTER = {
  name: "Starter",
  tagline: "Everything, unlocked",
  inr: "₹1,999",
  usd: "$19.99",
  features: [
    { label: "15 scans / month", strong: true },
    { label: "Up to 15 keywords per scan", strong: true },
    { label: "1 brand · re-import anytime", strong: false },
    { label: "All 4 Discovery Lenses", strong: false },
    { label: "Open source posts + save leads", strong: false },
    { label: "Kanban lead board + export", strong: false },
  ],
};

export default function Pricing() {
  const [headRef, headVisible] = useReveal();
  const [cardRef, cardVisible] = useReveal(0.15);
  const [currency, setCurrency] = useState("INR"); // INR | USD

  const price = currency === "INR" ? STARTER.inr : STARTER.usd;
  const subline = currency === "INR" ? "GST included" : "billed in USD";

  return (
    <section id="pricing" className="max-w-3xl mx-auto px-5 md:px-10 py-20 md:py-24 lm-cv">
      <div ref={headRef} className={`max-w-2xl mx-auto text-center mb-10 lm-reveal ${headVisible ? "is-visible" : ""}`}>
        <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-lm-primary mb-5 px-3.5 py-1.5 rounded-full bg-lm-surface border border-lm-border">
          <Zap className="w-3.5 h-3.5" strokeWidth={2.4} fill="currentColor" />
          Pricing
        </div>
        <h2 className="text-3xl md:text-5xl font-display font-semibold leading-tight tracking-tight">
          One simple plan. No maze.
        </h2>
        <p className="mt-4 text-lm-text2 leading-relaxed max-w-xl mx-auto">
          One plan unlocks everything —
          15 scans a month, up to 15 keywords each. That's it.
        </p>

        {/* Currency toggle */}
        <div
          data-testid="landing-pricing-currency"
          className="inline-flex items-center rounded-full border border-lm-border bg-lm-surface p-0.5 mt-8"
        >
          {[
            { key: "INR", label: "₹ INR" },
            { key: "USD", label: "$ USD" },
          ].map((o) => (
            <button
              key={o.key}
              data-testid={`landing-currency-${o.key}`}
              onClick={() => setCurrency(o.key)}
              className={`px-5 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                currency === o.key ? "bg-lm-primary text-white" : "text-lm-text2 hover:text-lm-text"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Single Starter card */}
      <div
        ref={cardRef}
        data-testid="landing-pricing-card-starter"
        className={`max-w-md mx-auto group relative rounded-2xl overflow-hidden flex flex-col border border-lm-primary/30 ring-1 ring-lm-primary/25 bg-lm-surface p-7 md:p-8 shadow-[0_18px_40px_-20px_rgba(31,74,56,0.22)] transition-all hover:-translate-y-1 hover:shadow-[0_28px_60px_-24px_rgba(31,74,56,0.28)] lm-reveal ${cardVisible ? "is-visible" : ""}`}
      >
        <div className="absolute top-5 right-5 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-lm-primary text-white px-2 py-1 rounded font-semibold">
          Most popular
        </div>

        <div className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-lg bg-lm-tint2 text-lm-primary flex items-center justify-center">
            <Zap className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <div>
            <div className="text-lg font-display font-semibold text-lm-text leading-tight">{STARTER.name}</div>
            <div className="text-[11px] text-lm-text3 font-mono uppercase tracking-widest">{STARTER.tagline}</div>
          </div>
        </div>

        <div className="mb-6 pb-6 border-b border-lm-border">
          <div className="flex items-baseline gap-1.5">
            <div data-testid="landing-starter-price" className="text-5xl font-display font-bold tracking-tight text-lm-text">{price}</div>
            <span className="text-sm text-lm-text3 font-mono">/month</span>
          </div>
          <div className="mt-1 text-xs text-lm-text3 font-mono">{subline}</div>
        </div>

        <ul className="space-y-3 mb-7 flex-1">
          {STARTER.features.map((f) => (
            <li key={f.label} className="text-sm flex items-start gap-2.5">
              <span className="mt-0.5 shrink-0 w-4 h-4 rounded-full bg-lm-tint2 text-lm-primary flex items-center justify-center">
                <Check className="w-2.5 h-2.5" strokeWidth={3.4} />
              </span>
              <span className={f.strong ? "font-semibold text-lm-text" : "text-lm-text"}>{f.label}</span>
            </li>
          ))}
        </ul>

        <Link
          to="/login"
          data-testid="pricing-cta-starter"
          className="inline-flex w-full items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold bg-lm-primary text-white hover:bg-lm-primaryHover hover:-translate-y-0.5 transition-all shadow-[0_10px_24px_-10px_rgba(31,74,56,0.5)]"
        >
          Get started
          <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
        </Link>
        <div className="mt-3 text-center text-[11px] text-lm-text3 font-medium">
          Your first scan is free · cancel anytime
        </div>
      </div>

      <div className="max-w-md mx-auto mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        {["First scan free", "GST included (₹)", "Cancel anytime"].map((t) => (
          <div key={t} className="flex items-center justify-center gap-2 rounded-md bg-lm-surface border border-lm-border py-3 px-4">
            <Check className="w-4 h-4 text-lm-primary shrink-0" strokeWidth={2.8} />
            <span className="font-medium text-lm-text">{t}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
